import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { catalogService } from '../services/catalog';
import { paymentService } from '../services/payment';
import { useApiError } from '../hooks/useApiError';
import { useToast } from '../components/Toast';
import { Solution } from '../types/solution';
import { BillingInformationForm } from '../components/BillingInformationForm';
import { ArrowLeft, Loader2, Globe } from 'lucide-react';
import { detectCountryFromIP, getCurrencyForCountry, formatCurrency, getCountryName } from '../utils/currency';
import { useConversionTracking } from '../hooks/useRevenueTracking';
import { fetchAuthSession } from 'aws-amplify/auth';

const CheckoutPage: React.FC = () => {
  const { solutionId: pathSolutionId } = useParams<{ solutionId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { trackCheckoutStarted } = useConversionTracking();
  
  // Get solutionId from either path params or query params (for FAISS compatibility)
  const solutionId = pathSolutionId || searchParams.get('solution');
  
  const [solution, setSolution] = useState<Solution | null>(null);
  const { error, isLoading, executeWithErrorHandling } = useApiError();
  const { error: showError } = useToast();
  const [processing, setProcessing] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showBillingForm, setShowBillingForm] = useState(false);
  const [billingInfo, setBillingInfo] = useState<any>(null);
  const [selectedGateway, setSelectedGateway] = useState<'cashfree' | 'payu'>('cashfree');
  const [detectedCountry, setDetectedCountry] = useState<string>('');
  const [countryDetecting, setCountryDetecting] = useState<boolean>(true);
  const [selectedCurrency, setSelectedCurrency] = useState<string>('USD');
  const [displayAmount, setDisplayAmount] = useState<number>(0);
  const [priceBreakdown, setPriceBreakdown] = useState<{
    baseAmount: number;
    discountAmount?: number;
    subtotal?: number;
    cgst?: number;
    sgst?: number;
    utgst?: number;
    igst?: number;
    gstRate: number;
    totalAmount: number;
    isIntrastate: boolean;
    isUT: boolean;
  } | null>(null);
  
  // Discount state
  const [discountCode, setDiscountCode] = useState<string>('');
  const [discountApplied, setDiscountApplied] = useState<any>(null);
  const [discountError, setDiscountError] = useState<string>('');
  const [validatingDiscount, setValidatingDiscount] = useState(false);
  
  // Country mismatch modal state
  const [showCountryMismatchModal, setShowCountryMismatchModal] = useState(false);
  const [confirmedTaxResidence, setConfirmedTaxResidence] = useState(false);

  useEffect(() => {
    if (!solutionId) {
      navigate('/catalog');
      return;
    }

    loadSolution();
    detectUserCountry();
  }, [solutionId, navigate]);

  // Track checkout started when solution loads
  useEffect(() => {
    if (solution && user) {
      const proTier = (solution.pricing as any)?.tiers?.find((tier: any) => tier.name === 'Pro');
      const amount = proTier?.amount || (solution.pricing as any)?.pro?.price || 0;
      trackCheckoutStarted(solution.solutionId, 'pro', amount);
    }
  }, [solution, user, trackCheckoutStarted]);

  // Update display amount when solution or currency changes
  useEffect(() => {
    if (!solution) return;
    
    const proTier = (solution.pricing as any)?.tiers?.find((tier: any) => tier.name === 'Pro');
    const displayPrice = (proTier as any)?.displayPrice || (solution.pricing as any)?.pro?.displayPrice || 0;
    
    // Show USD display price
    setDisplayAmount(displayPrice);
  }, [solution]);

  const detectUserCountry = async () => {
    try {
      setCountryDetecting(true);
      const country = await detectCountryFromIP();
      setDetectedCountry(country);
      // Always show USD
      setSelectedCurrency('USD');
    } catch (error) {
      console.error('Country detection failed:', error);
      // Don't set default - leave empty so user must select in billing form
      setDetectedCountry('');
      setSelectedCurrency('USD');
    } finally {
      setCountryDetecting(false);
    }
  };

  const loadSolution = async () => {
    await executeWithErrorHandling(
      () => catalogService.getSolution(solutionId!),
      {
        onSuccess: (data) => setSolution(data as Solution),
        onError: (error) => showError('Failed to load solution', error.message)
      }
    );
  };

  const handlePurchase = async () => {
    if (!solution || !user || !agreedToTerms) return;
    
    // Show billing form first
    setShowBillingForm(true);
  };

  const handleBillingSubmit = async (submittedBillingInfo: any) => {
    if (!solution || !user) return;

    setBillingInfo(submittedBillingInfo);
    setShowBillingForm(false);

    // Keep USD for display - backend handles INR conversion
    // setSelectedCurrency('INR'); // Removed - keep USD

    // Auto-select PayU for international billing
    if (submittedBillingInfo.billingCountry !== 'IN') {
      setSelectedGateway('payu');
    }

    // Check for country mismatch
    if (detectedCountry && submittedBillingInfo.billingCountry !== detectedCountry) {
      setShowCountryMismatchModal(true);
      return; // Don't proceed until user confirms
    }

    await recalculatePriceWithDiscount(submittedBillingInfo);
  };

  const handleValidateDiscount = async () => {
    if (!discountCode.trim() || !solution || !user) return;

    setValidatingDiscount(true);
    setDiscountError('');

    try {
      const proTier = solution.pricing?.tiers?.find(tier => tier.name === 'Pro');
      const baseAmount = proTier?.amount || solution.pricing?.pro?.price;

      if (!baseAmount) {
        setDiscountError('Unable to determine price for this solution');
        setValidatingDiscount(false);
        return;
      }

      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      const response = await fetch(`${import.meta.env.VITE_API_URL.replace(/\/$/, '')}/payments/discount/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          code: discountCode.toUpperCase(),
          userEmail: user.email,
          solutionId: solution.solutionId,
          tier: 'pro',
          amount: baseAmount
        })
      });

      const data = await response.json();

      if (data.valid) {
        console.log('✓ Discount valid:', data.discount);
        setDiscountApplied(data.discount);
        setDiscountError('');
        // Recalculate price with discount - pass discount directly to avoid state race condition
        const billingData = billingInfo || {
          billingCountry: 'IN',
          billingState: 'Karnataka'
        };
        console.log('Recalculating with billing data:', billingData);
        await recalculatePriceWithDiscount(billingData, data.discount);
      } else {
        setDiscountError(data.message || 'Invalid discount code');
        setDiscountApplied(null);
      }
    } catch (error) {
      setDiscountError('Failed to validate discount code');
      setDiscountApplied(null);
    } finally {
      setValidatingDiscount(false);
    }
  };

  const handleRemoveDiscount = async () => {
    setDiscountCode('');
    setDiscountApplied(null);
    setDiscountError('');
    // Recalculate price without discount
    if (billingInfo) {
      await recalculatePriceWithDiscount(billingInfo);
    } else {
      // If no billing info yet, calculate with default (India, Karnataka)
      await recalculatePriceWithDiscount({
        billingCountry: 'IN',
        billingState: 'Karnataka'
      });
    }
  };

  const recalculatePriceWithDiscount = async (billingData: any, discount?: any) => {
    if (!solution) return;

    // Use passed discount or state discount
    const activeDiscount = discount || discountApplied;
    console.log('🔄 Recalculating price...', { billingData, activeDiscount });

    const proTier = solution.pricing?.tiers?.find(tier => tier.name === 'Pro');
    let baseAmount = proTier?.amount || solution.pricing?.pro?.price || solution.pricing?.proTier?.amount || solution.pricing?.amount;
    
    if (!baseAmount) {
      console.error('Unable to determine base amount for solution:', solution);
      return;
    }

    console.log('Base amount:', baseAmount);

    // Apply discount if present - use discountAmount from validator
    let discountAmount = 0;
    if (activeDiscount) {
      console.log('🎫 Active discount object:', JSON.stringify(activeDiscount, null, 2));
      // Use the discountAmount calculated by the backend validator
      discountAmount = activeDiscount.discountAmount || 0;
      console.log('🎫 Using discountAmount from validator:', discountAmount);
    }

    console.log('Discount amount:', discountAmount);

    const discountedAmount = baseAmount - discountAmount;
    console.log('Discounted amount (subtotal):', discountedAmount);

    // Always use INR - no conversion needed (PayU handles it)
    
    // GST only applies to India - calculated on discounted amount
    let cgst = 0;
    let sgst = 0;
    let utgst = 0;
    let igst = 0;
    let gstRate = 0;
    let isIntrastate = false;
    let isUT = false;

    if (billingData.billingCountry === 'IN') {
      gstRate = 18;
      const supplierState = 'Karnataka'; // Your business is in Karnataka
      const customerState = billingData.billingState;
      
      // Union Territories without legislature
      const unionTerritories = ['Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu', 'Lakshadweep', 'Ladakh'];
      isUT = unionTerritories.includes(customerState);

      if (supplierState === customerState) {
        // Intrastate: CGST + SGST/UTGST
        isIntrastate = true;
        cgst = Math.round((discountedAmount * 9) / 100 * 100) / 100; // 9%
        if (isUT) {
          utgst = Math.round((discountedAmount * 9) / 100 * 100) / 100; // 9% UTGST
        } else {
          sgst = Math.round((discountedAmount * 9) / 100 * 100) / 100; // 9% SGST
        }
      } else {
        // Interstate: IGST
        isIntrastate = false;
        igst = Math.round((discountedAmount * 18) / 100 * 100) / 100; // 18%
      }
    }

    const totalAmount = discountedAmount + cgst + sgst + utgst + igst;

    setPriceBreakdown({
      baseAmount: baseAmount,  // Always store original price
      discountAmount: discountAmount,  // Store discount separately
      subtotal: discountedAmount,  // Subtotal after discount
      cgst: cgst > 0 ? cgst : undefined,
      sgst: sgst > 0 ? sgst : undefined,
      utgst: utgst > 0 ? utgst : undefined,
      igst: igst > 0 ? igst : undefined,
      gstRate,
      totalAmount,
      isIntrastate,
      isUT
    });
  };

  const handleProceedToPayment = async () => {
    if (!solution || !user || !billingInfo || !priceBreakdown) return;

    // Validate: Cashfree only for India
    if (billingInfo.billingCountry !== 'IN' && selectedGateway === 'cashfree') {
      showError('Cashfree is only available for India. Please use PayU for international payments.');
      return;
    }

    setProcessing(true);

    const paymentData = {
      ...billingInfo,
      baseAmount: priceBreakdown.baseAmount,
      cgst: priceBreakdown.cgst,
      sgst: priceBreakdown.sgst,
      utgst: priceBreakdown.utgst,
      igst: priceBreakdown.igst,
      gstRate: priceBreakdown.gstRate,
      sacCode: '998315',
      gateway: selectedGateway,
      currency: getCurrencyForCountry(billingInfo.billingCountry), // Send target currency for PayU
      discountCode: discountApplied?.code || null,
      discountAmount: priceBreakdown.discountAmount || 0
    };

    await executeWithErrorHandling(
      () => paymentService.initiatePayment(solution.solutionId, priceBreakdown.baseAmount, solution.name, paymentData, selectedGateway)
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex items-center space-x-2">
          <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
          <span className="text-gray-600">Loading checkout...</span>
        </div>
      </div>
    );
  }

  if (error && !solution) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 mb-4">{error.message}</div>
          <button
            onClick={() => navigate('/catalog')}
            className="text-blue-600 hover:text-blue-800"
          >
            Return to Catalog
          </button>
        </div>
      </div>
    );
  }

  if (!solution) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-600 mb-4">Solution not found</div>
          <button
            onClick={() => navigate('/catalog')}
            className="text-blue-600 hover:text-blue-800"
          >
            Return to Catalog
          </button>
        </div>
      </div>
    );
  }

  const isSubscription = solution.pricing.model === 'subscription';

  // If user is not authenticated, show login prompt
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Sign in to continue</h1>
            <p className="text-gray-600 mb-6">
              Please sign in to your account to complete the purchase of {solution?.name}.
            </p>
            <div className="space-y-4">
              <button
                onClick={() => navigate('/login', { 
                  state: { 
                    from: `/checkout?solution=${solutionId}&return_url=${searchParams.get('return_url')}` 
                  } 
                })}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-md font-medium hover:bg-blue-700"
              >
                Sign In
              </button>
              <button
                onClick={() => navigate('/register', { 
                  state: { 
                    from: `/checkout?solution=${solutionId}&return_url=${searchParams.get('return_url')}` 
                  } 
                })}
                className="w-full bg-gray-100 text-gray-700 py-2 px-4 rounded-md font-medium hover:bg-gray-200"
              >
                Create Account
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/catalog')}
            className="flex items-center text-gray-600 hover:text-gray-800 mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Solution
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Checkout</h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Solution Details - Takes 2 columns */}
          <div className="lg:col-span-2">
            <div className="card">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Order Summary</h2>
              
              <div className="flex items-start space-x-4 mb-6">
                <img
                  src={solution.assets?.images?.[0] || '/vite.svg'}
                  alt={solution.name}
                  className="w-16 h-16 rounded-lg object-cover"
                />
                <div className="flex-1">
                  <h3 className="font-medium text-gray-900">{solution.name}</h3>
                  <p className="text-sm text-gray-600 mt-1">{solution.category}</p>
                  <p className="text-sm text-gray-500 mt-2 line-clamp-2">
                    {solution.description}
                  </p>
                </div>
              </div>

              <div className="border-t pt-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-600">
                    Monthly Pro Subscription (1 user)
                  </span>
                  <span className="font-medium">{formatCurrency(displayAmount, selectedCurrency)}/user</span>
                </div>
                
                {isSubscription && (
                  <div className="text-sm text-gray-500 mb-4">
                    Individual plan. Monthly subscription. Cancel anytime.
                    <br />
                    <span className="text-xs">Need a team plan? <a href="mailto:support@cloudnestle.com" className="text-blue-600 hover:underline">Contact us</a></span>
                  </div>
                )}

                <div className="border-t pt-4">
                  <div className="flex justify-between items-center text-lg font-semibold">
                    <span>Total</span>
                    <span>{formatCurrency(displayAmount, selectedCurrency)}</span>
                  </div>
                </div>

                {/* GST/Export Information Notice - Show placeholder before, actual info after billing country is selected */}
                {billingInfo?.billingCountry ? (
                  billingInfo.billingCountry === 'IN' ? (
                    <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-start gap-2">
                        <span className="text-blue-600 text-lg">ℹ️</span>
                        <div className="text-sm space-y-1 flex-1">
                          <p className="font-semibold text-blue-900">GST Information</p>
                          <p className="text-gray-700">• GST will be added to the base price</p>
                          <p className="text-gray-700">• B2B invoices with ITC: Select 'Business' and provide GSTIN in Billing Info form</p>
                          <p className="text-gray-700">• Claim Input Tax Credit (ITC) for business purchases</p>
                          <div className="mt-2 text-[10px] text-gray-600">
                            <div>Our GSTIN:</div>
                            <div className="font-semibold text-gray-900 text-[9px]">29ADWPA6289Q1ZB</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-start gap-2">
                        <span className="text-green-600 text-lg">🌍</span>
                        <div className="text-sm space-y-1">
                          <p className="font-semibold text-green-900">Export Information</p>
                          <p className="text-gray-700">• Zero-rated export (No GST applicable)</p>
                          <p className="text-gray-700">• Price shown in USD, billed in INR (₹900)</p>
                          <p className="text-gray-700">• Your bank converts INR to your local currency</p>
                          <p className="text-gray-700">• Invoice includes LUT ARN & Purpose Code P0802</p>
                          <p className="text-xs text-gray-600 mt-2">
                            LUT ARN: <span className="font-mono font-semibold">AD291225033708W</span>
                          </p>
                        </div>
                      </div>
                    </div>
                  )
                ) : (
                  <div className="mt-4 bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start gap-2">
                      <span className="text-gray-600 text-lg">💡</span>
                      <div className="text-sm space-y-1">
                        <p className="font-semibold text-gray-900">Tax & Currency Information</p>
                        <p className="text-gray-600">GST and currency details will be shown after you enter your billing address.</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Payment Form - Takes 2 columns */}
          <div className="lg:col-span-2">
            <div className="card">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Payment Details</h2>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
                  <div className="text-red-800 text-sm break-words">{error.message}</div>
                </div>
              )}

              {/* User Information */}
              <div className="mb-6">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-sm font-medium text-gray-700">Billing Information</h3>
                  {billingInfo && (
                    <button
                      onClick={() => setShowBillingForm(true)}
                      className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                    >
                      Edit
                    </button>
                  )}
                </div>
                <div className="bg-gray-50 rounded-md p-4 space-y-3">
                  <div className="text-sm">
                    <div className="text-xs text-gray-500 uppercase tracking-wide">Email Address</div>
                    <div className="font-medium text-gray-900 break-all text-xs">{user?.email}</div>
                  </div>
                  <div className="text-sm">
                    <div className="text-xs text-gray-500 uppercase tracking-wide">Name</div>
                    <div className="font-medium text-gray-900 break-words text-xs">{user?.profile?.name?.replace(/ Name$/, '') || 'Not provided'}</div>
                  </div>
                  {billingInfo ? (
                    <>
                      {billingInfo.companyName && (
                        <div className="text-sm">
                          <div className="text-xs text-gray-500 uppercase tracking-wide">Company</div>
                          <div className="font-medium text-gray-900 break-words text-xs">{billingInfo.companyName}</div>
                        </div>
                      )}
                      {billingInfo.gstin && (
                        <div className="text-sm">
                          <div className="text-xs text-gray-500 uppercase tracking-wide">GSTIN</div>
                          <div className="font-medium text-gray-900 break-words text-xs">{billingInfo.gstin}</div>
                        </div>
                      )}
                      <div className="text-sm">
                        <div className="text-xs text-gray-500 uppercase tracking-wide">Phone</div>
                        <div className="font-medium text-gray-900 break-words text-xs">
                          {billingInfo.phoneCountryCode} {billingInfo.phoneNumber}
                        </div>
                      </div>
                      <div className="text-sm">
                        <div className="text-xs text-gray-500 uppercase tracking-wide">Address</div>
                        <div className="font-medium text-gray-900 break-words text-xs">
                          {billingInfo.billingAddress}<br />
                          {billingInfo.billingCity}, {billingInfo.billingState} {billingInfo.billingPostalCode}<br />
                          {getCountryName(billingInfo.billingCountry)}
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      {user?.profile?.company && (
                        <div className="text-sm">
                          <div className="text-xs text-gray-500 uppercase tracking-wide">Company</div>
                          <div className="font-medium text-gray-900 break-words text-xs">{user.profile.company}</div>
                        </div>
                      )}
                      <div className="text-sm">
                        <div className="text-xs text-gray-500 uppercase tracking-wide">Location</div>
                        <div className="font-medium text-gray-900 break-words text-xs">
                          {countryDetecting ? (
                            <span className="text-gray-400">Detecting location...</span>
                          ) : billingInfo ? (
                            getCountryName(billingInfo.billingCountry)
                          ) : detectedCountry ? (
                            getCountryName(detectedCountry)
                          ) : (
                            <span className="text-gray-500">Will be set after billing address</span>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Payment Gateway Selection */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-700 mb-3">Available Payment Gateway Providers</h3>
                {billingInfo && (
                  <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-xs text-blue-800">
                      <strong>💡 Recommendation:</strong> {billingInfo.billingCountry === 'IN' 
                        ? 'For Indian billing address, we recommend using Cashfree for faster processing.' 
                        : 'For international billing address, we recommend using PayU for better currency support.'}
                    </p>
                  </div>
                )}
                <div className="space-y-3">
                  <label className={`flex items-center p-4 border-2 rounded-lg transition-colors ${billingInfo && billingInfo.billingCountry !== 'IN' ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-50'}`} style={{ borderColor: selectedGateway === 'cashfree' ? '#2563eb' : '#e5e7eb' }}>
                    <input
                      type="radio"
                      name="gateway"
                      value="cashfree"
                      checked={selectedGateway === 'cashfree'}
                      onChange={(e) => setSelectedGateway(e.target.value as 'cashfree' | 'payu')}
                      disabled={billingInfo && billingInfo.billingCountry !== 'IN'}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                    />
                    <img 
                      src="/images/cashfree.png" 
                      alt="Cashfree" 
                      className="h-12 w-20 object-contain mx-3"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900">
                        Cashfree (Recommended)
                        {billingInfo && billingInfo.billingCountry !== 'IN' && (
                          <span className="text-red-600 text-xs ml-2">(India only)</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500">Cards, UPI, Net Banking, Wallets</div>
                      <div className="text-xs text-orange-600 font-medium mt-1">🇮🇳 Domestic payments only (India)</div>
                    </div>
                  </label>
                  
                  <label className="flex items-center p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors" style={{ borderColor: selectedGateway === 'payu' ? '#2563eb' : '#e5e7eb' }}>
                    <input
                      type="radio"
                      name="gateway"
                      value="payu"
                      checked={selectedGateway === 'payu'}
                      onChange={(e) => setSelectedGateway(e.target.value as 'cashfree' | 'payu')}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                    />
                    <div className="h-12 w-20 mx-3 flex items-center justify-center">
                      <img 
                        src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcS2EMrhqFYHWyEhY8yxnScriEXG3UR6uaY-yg&s" 
                        alt="PayU" 
                        className="h-10 object-contain"
                      />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900">PayU</div>
                      <div className="text-xs text-gray-500">International payments supported</div>
                      <div className="text-xs text-green-600 font-medium mt-1">🌍 Accepts payments worldwide</div>
                    </div>
                  </label>
                </div>

                {/* International Billing Notice */}
                {billingInfo?.billingCountry && billingInfo.billingCountry !== 'IN' && (
                  <>
                    <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <div className="flex items-start gap-2">
                        <span className="text-blue-600 text-lg">ℹ️</span>
                        <p className="text-sm text-gray-700">
                          International billing detected: PayU has been selected for currency conversion. Cashfree is available for India only.
                        </p>
                      </div>
                    </div>
                    
                    {/* Tax Disclaimer for International Purchases */}
                    <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <div className="flex items-start gap-2">
                        <span className="text-blue-600 text-lg">💡</span>
                        <div className="text-sm text-gray-700">
                          <p className="font-medium mb-1">International Purchase</p>
                          <p>Price shown is exclusive of any local taxes in your country. You may be responsible for VAT/GST/Sales Tax under local regulations.</p>
                        </div>
                      </div>
                    </div>
                  </>
                )}
                
                {/* Payment Method Icons */}
                <div className="mt-4 p-3 bg-gray-50 rounded-md">
                  <div className="text-xs text-gray-500 mb-2">Accepted Payment Methods:</div>
                  <div className="grid grid-cols-4 gap-2">
                    <img src="/images/visa.png" alt="Visa" className="h-6 object-contain" />
                    <img src="/images/mastercard.png" alt="Mastercard" className="h-6 object-contain" />
                    <img src="/images/amex.png" alt="American Express" className="h-6 object-contain" />
                    <img src="/images/maestro.png" alt="Maestro" className="h-6 object-contain" />
                    <img src="/images/diners.png" alt="Diners Club" className="h-6 object-contain" />
                    <img src="/images/gpay.png" alt="Google Pay" className="h-6 object-contain" />
                    <img src="/images/phonepe.png" alt="PhonePe" className="h-6 object-contain" />
                    <img src="/images/bhim.png" alt="BHIM UPI" className="h-6 object-contain" />
                  </div>
                </div>
              </div>

              {/* Terms Agreement */}
              <div className="mb-6">
                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id="terms-agreement"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="terms-agreement" className="text-sm text-gray-600">
                    I agree to the{' '}
                    <a 
                      href="/terms" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 underline"
                    >
                      Terms of Service
                    </a>
                    {' '}and{' '}
                    <a 
                      href="/privacy" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 underline"
                    >
                      Privacy Policy
                    </a>
                    .{isSubscription && ' I understand that my subscription will auto-renew monthly.'}
                  </label>
                </div>
              </div>

              {/* Purchase Button */}
              {!priceBreakdown ? (
                <button
                  onClick={handlePurchase}
                  disabled={processing || !agreedToTerms}
                  className="w-full bg-blue-600 text-white py-3 px-4 rounded-md font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {processing ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Processing...
                    </>
                  ) : (
                    <>
                      Enter Billing Address
                    </>
                  )}
                </button>
              ) : (
                <>
                  {/* Discount Code Section */}
                  <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-3">Have a discount code?</h4>
                    {!discountApplied ? (
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={discountCode}
                          onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                          placeholder="Enter code"
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          disabled={validatingDiscount}
                        />
                        <button
                          onClick={handleValidateDiscount}
                          disabled={!discountCode.trim() || validatingDiscount}
                          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                        >
                          {validatingDiscount ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin mr-1" />
                              Validating...
                            </>
                          ) : (
                            'Apply'
                          )}
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-md">
                        <div>
                          <div className="font-medium text-green-800">{discountApplied.code} Applied!</div>
                          <div className="text-sm text-green-600">
                            {discountApplied.type === 'percentage' 
                              ? `${discountApplied.value}% off` 
                              : `₹${discountApplied.value} off`}
                          </div>
                        </div>
                        <button
                          onClick={handleRemoveDiscount}
                          className="text-red-600 hover:text-red-800 text-sm font-medium"
                        >
                          Remove
                        </button>
                      </div>
                    )}
                    {discountError && (
                      <div className="mt-2 text-sm text-red-600">{discountError}</div>
                    )}
                  </div>

                  {/* GST Breakdown */}
                  <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-gray-900">Payment Breakdown</h4>
                      {priceBreakdown.gstRate > 0 && (
                        <img src="/images/gstlogo.png" alt="GST" className="h-8 object-contain" />
                      )}
                    </div>
                    <div className="space-y-2 text-sm">
                      {discountApplied && (
                        <>
                          <div className="flex justify-between text-gray-400">
                            <span className="line-through">Original Amount:</span>
                            <span className="line-through font-medium">{formatCurrency((solution.pricing?.tiers?.find(t => t.name === 'Pro')?.amount || solution.pricing?.pro?.price || 0), 'INR')}</span>
                          </div>
                          <div className="flex justify-between text-green-600">
                            <span>Discount ({discountApplied.type === 'percentage' ? `${discountApplied.value}%` : 'Fixed'}):</span>
                            <span className="font-medium">-{formatCurrency(discountApplied.discountAmount, 'INR')}</span>
                          </div>
                          <div className="pt-1 border-t border-gray-200"></div>
                        </>
                      )}
                      <div className="flex justify-between">
                        <span className="text-gray-600">{discountApplied ? 'Subtotal:' : 'Base Amount:'}</span>
                        <span className="font-medium">{formatCurrency(discountApplied ? (priceBreakdown.subtotal || priceBreakdown.baseAmount) : priceBreakdown.baseAmount, 'INR')}</span>
                      </div>
                      {priceBreakdown.gstRate > 0 && (
                        <>
                          <div className="text-xs text-gray-500 mt-2">SAC Code: 998315 (SaaS/OIDAR Services)</div>
                          {priceBreakdown.isIntrastate ? (
                            <>
                              <div className="flex justify-between">
                                <span className="text-gray-600">CGST (9%):</span>
                                <span className="font-medium">{formatCurrency(priceBreakdown.cgst!, 'INR')}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">{priceBreakdown.isUT ? 'UTGST' : 'SGST'} (9%):</span>
                                <span className="font-medium">{formatCurrency((priceBreakdown.sgst || priceBreakdown.utgst)!, 'INR')}</span>
                              </div>
                            </>
                          ) : (
                            <div className="flex justify-between">
                              <span className="text-gray-600">IGST (18%):</span>
                              <span className="font-medium">{formatCurrency(priceBreakdown.igst!, 'INR')}</span>
                            </div>
                          )}
                          {billingInfo?.isBusinessPurchase && billingInfo?.gstin ? (
                            <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-xs text-green-800">
                              ✓ GSTIN provided. You can claim Input Tax Credit (ITC) on this purchase.
                            </div>
                          ) : billingInfo?.isBusinessPurchase && !billingInfo?.gstin ? (
                            <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800">
                              ⚠️ No GSTIN provided. You cannot claim Input Tax Credit (ITC) on this purchase. If you're a registered business, please provide your GSTIN.
                            </div>
                          ) : (
                            <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800">
                              ℹ️ Personal purchase. GST is included but you cannot claim Input Tax Credit (ITC).
                            </div>
                          )}
                        </>
                      )}
                      <div className="flex justify-between pt-2 border-t border-blue-300">
                        <span className="font-semibold text-gray-900">Total Amount:</span>
                        <span className="font-bold text-lg text-blue-600">{formatCurrency(priceBreakdown.totalAmount, 'INR')}</span>
                      </div>
                      <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-start space-x-2">
                          <Globe className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
                          <p className="text-xs text-blue-800">
                            <strong>💳 Payment Processing:</strong> Your payment will be processed in your local currency. The payment gateway will automatically convert the amount based on current exchange rates.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* International Payment Notice - Show for non-India customers */}
                  {billingInfo?.billingCountry && billingInfo.billingCountry !== 'IN' && (
                    <div className="mb-4 p-4 bg-green-50 border-2 border-green-300 rounded-lg">
                      <div className="flex items-start gap-3">
                        <span className="text-green-600 text-2xl flex-shrink-0">✓</span>
                        <div className="flex-1">
                          <h4 className="font-semibold text-green-900 mb-2">International Payment</h4>
                          <ul className="text-sm text-gray-700 space-y-1.5">
                            <li className="flex items-start">
                              <span className="mr-2">•</span>
                              <span>You'll see the exact amount in your local currency at checkout</span>
                            </li>
                            <li className="flex items-start">
                              <span className="mr-2">•</span>
                              <span>No surprise fees or bank conversion charges</span>
                            </li>
                            <li className="flex items-start">
                              <span className="mr-2">•</span>
                              <span>Pay in USD, EUR, GBP, or your preferred currency</span>
                            </li>
                            <li className="flex items-start">
                              <span className="mr-2">•</span>
                              <span>The amount you see is the amount you pay</span>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}

                  <button
                    onClick={handleProceedToPayment}
                    disabled={processing}
                    className="w-full bg-blue-600 text-white py-3 px-4 rounded-md font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {processing ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        Processing...
                      </>
                    ) : (
                      <>
                        Proceed to Pay
                        <span className="ml-2">{formatCurrency(priceBreakdown.totalAmount, 'INR')}</span>
                      </>
                    )}
                  </button>
                </>
              )}

              {!agreedToTerms && (
                <div className="mt-2 text-xs text-red-600 text-center">
                  Please agree to the terms and conditions to continue
                </div>
              )}
            </div>
          </div>

          {/* Security Information - Takes 1 column */}
          <div className="lg:col-span-1">
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">🔒 Secure & Encrypted Payment</h3>
              
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  Your payment information is encrypted and secure. We don't store your card details.
                </p>

                {/* Security Features - Made Prominent */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-3">
                  <div className="flex items-center space-x-3">
                    <img src="/images/ssl.jpg" alt="SSL" className="h-8 w-8 object-contain" />
                    <span className="text-sm font-bold text-green-800">SSL Secured</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <img src="/images/aes256.jpg" alt="AES 256" className="h-8 w-8 object-contain" />
                    <span className="text-sm font-bold text-green-800">256-bit Encryption</span>
                  </div>
                </div>

                {/* Compliance Badges - Made Bigger and Clickable */}
                <div className="pt-4 border-t border-gray-200">
                  <div className="text-sm font-bold text-gray-900 mb-4">🏆 Payment Gateway Compliance:</div>
                  <div className="space-y-4">
                    <div className="flex items-center space-x-3 p-2">
                      <img src="/images/pci-dss.jpg" alt="PCI DSS" className="h-10 w-10 object-contain" />
                      <div className="flex-1">
                        <div className="text-sm font-bold text-gray-800">
                          PCI DSS v4.0.1 Compliant
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-3 p-2">
                      <img src="/images/iso9001-2015.jpg" alt="ISO 9001" className="h-10 w-10 object-contain" />
                      <div className="flex-1">
                        <div className="text-sm font-bold text-gray-800">
                          ISO 9001:2015 Certified
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-3 p-2">
                      <img src="/images/iso27001-2022.jpg" alt="ISO 27001" className="h-10 w-10 object-contain" />
                      <div className="flex-1">
                        <div className="text-sm font-bold text-gray-800">
                          ISO/IEC 27001:2022 Certified
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Company Information */}
            <div className="card mt-6">
              <h3 className="text-lg font-semibold mb-4">Billed By</h3>
              <div className="flex items-start space-x-4 mb-4">
                <img 
                  src="/cloudnestle-logo.png" 
                  alt="CloudNestle" 
                  className="h-12 w-12 object-contain flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900">CloudNestle Consulting & Services</p>
                  <p className="text-[11px] text-gray-600">GSTIN: <span className="font-semibold">29ADWPA6289Q1ZB</span></p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Billing Information Modal */}
        {showBillingForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <BillingInformationForm
                  onSubmit={handleBillingSubmit}
                  onBack={() => setShowBillingForm(false)}
                  initialData={billingInfo}
                  detectedCountry={detectedCountry}
                />
              </div>
            </div>
          </div>
        )}

        {/* Country Mismatch Modal */}
        {showCountryMismatchModal && billingInfo && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-lg w-full p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Location Verification Required
              </h3>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-gray-700 mb-3">
                  We noticed a mismatch between your billing address (<strong>{getCountryName(billingInfo.billingCountry)}</strong>) and current location (<strong>{getCountryName(detectedCountry)}</strong>).
                </p>
                <p className="text-sm text-gray-700">
                  To ensure you qualify for 0% GST under international export rules, we may occasionally request residency proof. No action is needed now, but please ensure your billing details are accurate.
                </p>
              </div>

              <label className="flex items-start space-x-3 mb-6 cursor-pointer">
                <input
                  type="checkbox"
                  checked={confirmedTaxResidence}
                  onChange={(e) => setConfirmedTaxResidence(e.target.checked)}
                  className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="text-sm text-gray-700">
                  I confirm this is my legal tax residence
                </span>
              </label>

              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowCountryMismatchModal(false);
                    setShowBillingForm(true);
                    setConfirmedTaxResidence(false);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Edit Billing Address
                </button>
                <button
                  onClick={async () => {
                    if (!confirmedTaxResidence) {
                      showError('Please confirm your tax residence to proceed');
                      return;
                    }
                    setShowCountryMismatchModal(false);
                    await recalculatePriceWithDiscount(billingInfo);
                  }}
                  disabled={!confirmedTaxResidence}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Proceed
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CheckoutPage;
